import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import userPreferencesService, {
  type GridSettingEntry,
  type UserPreferences,
} from '../services/userPreferencesService';

const SAVE_DEBOUNCE_MS = 800;

/**
 * Per-user saved grid view (UAT 2026-07-09, Walid to Ashton: "if you change
 * columns, if you sort it… anytime you log in, it's going to bring back your
 * own settings").
 *
 * Loads the user's preferences once per pharmacy (shared TanStack Query key —
 * every grid on the page reuses the same fetch) and exposes the entry for
 * `gridKey` plus a debounced, field-merging save. Saves target ONE grid key
 * on the server, so two tabs saving different grids never clobber each other.
 *
 * Persistence failures log a console warning and keep the in-session state —
 * grid layout is user preference, never patient data.
 */
export function useGridSettings(gridKey: string, pharmacyId?: string) {
  const queryClient = useQueryClient();
  const { data, isSuccess, isError } = useQuery<UserPreferences>({
    queryKey: ['user-preferences', pharmacyId],
    queryFn: () => userPreferencesService.getUserPreferences(pharmacyId as string),
    enabled: !!pharmacyId,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const entry: GridSettingEntry | undefined = isSuccess
    ? data?.gridSettings?.[gridKey]
    : undefined;

  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<GridSettingEntry>({});

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
    if (!pharmacyId || Object.keys(pending.current).length === 0) return;
    const payload = pending.current;
    pending.current = {};
    userPreferencesService
      .updateGridSetting(pharmacyId, gridKey, payload)
      .then((prefs) => queryClient.setQueryData(['user-preferences', pharmacyId], prefs))
      .catch((err: unknown) => {
        console.warn(
          `Grid settings save failed for ${gridKey}:`,
          (err as { message?: string })?.message ?? err,
        );
      });
  }, [pharmacyId, gridKey, queryClient]);

  const save = useCallback(
    (partial: GridSettingEntry) => {
      if (!pharmacyId) return;
      pending.current = { ...pending.current, ...partial };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [pharmacyId, flush],
  );

  // A change made within the debounce window must survive tab switches and
  // navigation — flush the pending payload on unmount instead of dropping it.
  useEffect(() => flush, [flush]);

  // `settled` flips true on success OR error — callers that gate their first
  // data fetch on the saved view must never deadlock behind a prefs outage
  // (grid layout is a preference; the queue itself must always load).
  return { entry, loaded: isSuccess, settled: isSuccess || isError, save };
}
